import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SubscriptionStatus = 'active' | 'cancelled' | 'inactive' | 'past_due' | 'trialing';
export type BillingCycle = 'monthly' | 'yearly';
export type BillingType = 'individual' | 'business';

export interface BillingInfo {
  billingType: BillingType;
  fullName: string;
  companyName: string;
  billingEmail: string;
  country: string;
  stateRegion: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  companyRegistrationNumber: string;
  taxVatNumber: string;
  isDefault: boolean;
}

export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'jcb'
  | 'diners'
  | 'unionpay'
  | 'maestro'
  | 'generic';

export interface PaymentCard {
  id: string;
  brand: CardBrand;
  last4: string;
  expiry: string; // e.g. "12 / 2027"
  cardholderName: string;
  isDefault: boolean;
}

export type InvoiceStatus = 'PAID' | 'PENDING' | 'PROCESSING' | 'FAILED' | 'REFUNDED' | 'CANCELLED';

export interface InvoiceItem {
  id: string;
  invoiceNumber: string;
  date: string;
  description: string;
  plan: string;
  amount: string;
  status: InvoiceStatus;
  period: string;
}

export interface SupportTicket {
  id: string;
  topic: string;
  subject: string;
  message: string;
  screenshots: string[];
  createdAt: string;
}

interface SubscriptionState {
  status: SubscriptionStatus;
  hasTrial: boolean;
  trialDays: number;
  trialEndsDate: string;
  billingCycle: BillingCycle;
  planName: string;
  monthlyPrice: number;
  yearlyPrice: number;
  taxRate: number; // 0.10 for 10%
  nextBillingDate: string;
  accessEndsDate: string;
  gracePeriodEndsDate: string;
  cancellationReason: string;

  // Billing & Payment
  billingInfo: BillingInfo;
  paymentMethods: PaymentCard[];
  invoices: InvoiceItem[];

  // Promo code
  appliedPromo: { code: string; discountPercent: number } | null;

  // Support
  submittedTickets: SupportTicket[];

  // Actions
  setStatus: (status: SubscriptionStatus) => void;
  setBillingCycle: (cycle: BillingCycle) => void;
  setHasTrial: (hasTrial: boolean) => void;
  updateBillingInfo: (info: Partial<BillingInfo>) => void;
  addPaymentMethod: (card: Omit<PaymentCard, 'id'>) => void;
  updatePaymentMethod: (id: string, card: Partial<PaymentCard>) => void;
  removePaymentMethod: (id: string) => void;
  setDefaultPaymentMethod: (id: string) => void;
  cancelSubscription: (reason?: string, accessEndsDate?: string) => void;
  reactivateSubscription: (nextBillingDate?: string) => void;
  applyPromoCode: (
    code: string,
    discountPercent?: number,
  ) => { success: boolean; message: string; discountPercent: number };
  removePromoCode: () => void;
  addSupportTicket: (ticket: Omit<SupportTicket, 'id' | 'createdAt'>) => void;
  resetToDefaults: () => void;
}

const defaultBillingInfo: BillingInfo = {
  billingType: 'individual',
  fullName: '',
  companyName: '',
  billingEmail: '',
  country: '',
  stateRegion: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  companyRegistrationNumber: '',
  taxVatNumber: '',
  isDefault: true,
};

const defaultPaymentMethods: PaymentCard[] = [];

const defaultInvoices: InvoiceItem[] = [];

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set) => ({
      status: 'inactive',
      hasTrial: false,
      trialDays: 0,
      trialEndsDate: '',
      billingCycle: 'monthly',
      planName: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      taxRate: 0,
      nextBillingDate: '',
      accessEndsDate: '',
      gracePeriodEndsDate: '',
      cancellationReason: '',

      billingInfo: defaultBillingInfo,
      paymentMethods: defaultPaymentMethods,
      invoices: defaultInvoices,
      appliedPromo: null,
      submittedTickets: [],

      setStatus: (status) => set({ status }),
      setBillingCycle: (billingCycle) => set({ billingCycle }),
      setHasTrial: (hasTrial) => set({ hasTrial }),

      updateBillingInfo: (info) =>
        set((state) => ({
          billingInfo: { ...state.billingInfo, ...info },
        })),

      addPaymentMethod: (cardData) =>
        set((state) => {
          const newId = `card_${Date.now()}`;
          const isFirstCard = state.paymentMethods.length === 0;
          const shouldBeDefault = cardData.isDefault || isFirstCard;

          const updatedExisting = shouldBeDefault
            ? state.paymentMethods.map((c) => ({ ...c, isDefault: false }))
            : state.paymentMethods;

          const newCard: PaymentCard = {
            ...cardData,
            id: newId,
            isDefault: shouldBeDefault,
          };

          return {
            paymentMethods: [newCard, ...updatedExisting],
          };
        }),

      updatePaymentMethod: (id, cardData) =>
        set((state) => {
          const shouldBeDefault = cardData.isDefault;
          return {
            paymentMethods: state.paymentMethods.map((card) => {
              if (card.id === id) {
                return { ...card, ...cardData };
              }
              if (shouldBeDefault) {
                return { ...card, isDefault: false };
              }
              return card;
            }),
          };
        }),

      removePaymentMethod: (id) =>
        set((state) => {
          const remaining = state.paymentMethods.filter((c) => c.id !== id);
          if (remaining.length > 0 && !remaining.some((c) => c.isDefault)) {
            remaining[0].isDefault = true;
          }
          return { paymentMethods: remaining };
        }),

      setDefaultPaymentMethod: (id) =>
        set((state) => ({
          paymentMethods: state.paymentMethods.map((c) => ({
            ...c,
            isDefault: c.id === id,
          })),
        })),

      cancelSubscription: (reason, accessEndsDate) =>
        set((state) => ({
          status: 'cancelled',
          cancellationReason: reason || '',
          accessEndsDate: accessEndsDate || state.accessEndsDate || '',
        })),

      reactivateSubscription: (nextBillingDate) =>
        set((state) => ({
          status: 'active',
          nextBillingDate: nextBillingDate || state.nextBillingDate || '',
        })),

      applyPromoCode: (rawCode, discountPercent = 0) => {
        const code = rawCode.trim().toUpperCase();
        set({ appliedPromo: { code, discountPercent } });
        return {
          success: true,
          message: `Promo code ${code} applied!`,
          discountPercent,
        };
      },

      removePromoCode: () => set({ appliedPromo: null }),

      addSupportTicket: (ticket) =>
        set((state) => ({
          submittedTickets: [
            {
              ...ticket,
              id: `ticket_${Date.now()}`,
              createdAt: new Date().toISOString(),
            },
            ...state.submittedTickets,
          ],
        })),

      resetToDefaults: () =>
        set({
          status: 'inactive',
          hasTrial: false,
          trialDays: 0,
          trialEndsDate: '',
          billingCycle: 'monthly',
          planName: '',
          monthlyPrice: 0,
          yearlyPrice: 0,
          taxRate: 0,
          nextBillingDate: '',
          accessEndsDate: '',
          gracePeriodEndsDate: '',
          cancellationReason: '',
          billingInfo: defaultBillingInfo,
          paymentMethods: defaultPaymentMethods,
          invoices: defaultInvoices,
          appliedPromo: null,
          submittedTickets: [],
        }),
    }),
    {
      name: 'minipt-subscription-storage',
      partialize: (state) => {
        // Exclude appliedPromo from persistent storage so a promo code is never
        // retained in cache across transactions or browser reloads.
        const rest = { ...state };
        delete (rest as Partial<SubscriptionState>).appliedPromo;
        return rest;
      },
    },
  ),
);

export const clearSubscriptionData = (): void => {
  useSubscriptionStore.getState().resetToDefaults();
  try {
    useSubscriptionStore.persist?.clearStorage?.();
  } catch {
    // Ignore storage access errors
  }
  try {
    localStorage.removeItem('minipt-subscription-storage');
    sessionStorage.removeItem('minipt-subscription-storage');
  } catch {
    // Ignore storage access errors
  }
};
